import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { ActionHandlerFunction, AdminUser, OrgInfo } from "@preveil-api";
import { Account, AdminErrorMessages, AdminSuccessMessages, AdminUIActionTypes, Message, useAppDispatch, account_types, usePutPendingAccountTypeMutation } from "src/common";
import { Loading } from "src/components";
import { uiActions } from "src/store/ui-manager/slice";

type AllProps = {
  current_selected_user: AdminUser;
  current_account: Account;
  org_info: OrgInfo;
  show: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>
  handleAction: ActionHandlerFunction;
}

// Description: Modal to convert a user to an express account.
function ConvertToExpressModalComponent(props: AllProps) {
  const { current_selected_user, current_account, show, setShowModal, handleAction, org_info } = props;
  const dispatch = useAppDispatch();
  const [is_loading, setIsLoading] = useState<boolean>(false);
  const [putPendingAccountType] = usePutPendingAccountTypeMutation();
  const pending_conversion = current_selected_user.pending_account_type === account_types.express;
  const modal_text = pending_conversion ? "The user will remain a Full account." : <>An Express account <b>prevents</b> user access to the Preveil Mobile and Desktop applications.</>;
  const modal_confirm = pending_conversion ? "Would you like to cancel the conversion request?" : "Would you like to continue to convert this account?";
  
  // Description: set the user's pending account type to express to initiate the conversion request. Close the modal and refresh the page when successful.
  async function handleConvert() {
    setIsLoading(true);
    try {
      await putPendingAccountType({
        account_ids: Account.getAccountIdentifiers(current_account),
        body: {
          entity_id: org_info.org_id,
          users: [current_selected_user.user_id],
          account_type: account_types.express,
        },
      }).unwrap();
      setShowModal(false);
      handleAction({ actionType: AdminUIActionTypes.ShowSideNavMenu });
      handleAction({ actionType: AdminUIActionTypes.GetRefreshRequest });
      dispatch(uiActions.handleSetMessage(new Message(AdminSuccessMessages.success_convert_to_express)));
    } catch (error) {
      dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.error_convert_to_express), error));
    } finally {
      setIsLoading(false);
    }
  }

  // Description: set the user's pending account type to null to cancel the conversion request. Close the modal and refresh the page when successful.
  async function handleCancelConversion() {
    setIsLoading(true);
    try {
      await putPendingAccountType({
        account_ids: Account.getAccountIdentifiers(current_account),
        body: {
          entity_id: org_info.org_id,
          users: [current_selected_user.user_id],
          account_type: null,
        },
      }).unwrap();
      setShowModal(false);
      handleAction({ actionType: AdminUIActionTypes.ShowSideNavMenu });
      handleAction({ actionType: AdminUIActionTypes.GetRefreshRequest });
      dispatch(uiActions.handleSetMessage(new Message(AdminSuccessMessages.success_cancel_conversion)));
    } catch (error) {
      dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.error_cancel_conversion), error));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Modal show={show} onHide={() => setShowModal(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title> 
          {pending_conversion ? 
            <>Cancel Conversion Request for <b>{current_selected_user.display_name}</b>?</>
            :
            <>Convert <b>{current_selected_user.display_name}</b> to an Express account?</>
          }
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {is_loading ? <Loading className="in-place" /> : 
          <>
            <p>{modal_text}</p>
            <p>{modal_confirm}</p>
          </>
        }
      </Modal.Body>
      <Modal.Footer>
        {!is_loading &&
        <>
          {pending_conversion ? (
            <Button variant="primary" onClick={handleCancelConversion}>Cancel Request</Button>
          ) : (
            <Button variant="primary" onClick={handleConvert}>Convert</Button>
          )}
          <Button variant="no-outline-primary" onClick={() => setShowModal(false)}>Cancel</Button>
        </>
        }
      </Modal.Footer>
    </Modal>
  );
}

export default React.memo(ConvertToExpressModalComponent);
